import * as React from 'react';
import { useCallback } from 'react';

import loggerSingleton from 'js/app/loggerSingleton';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import CourseraPlusProductVariants from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { SUBSCRIBE_TO_COURSERA_PLUS } from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import { SKIP_ONBOARDING_QUERY_PARAM } from 'bundles/onboarding-common/constants';

export const useEnrollInOrUpgradeToCourseraPlus = (
  collectionId?: string,
  shouldUseHookForPostAuthCheckout?: boolean
) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(false);

  const checkoutCourseraPlus = useCallback((productItemId: string, resolvedCollectionId?: string) => {
    const isCollectionsCheckout = !!resolvedCollectionId;
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[SUBSCRIBE_TO_COURSERA_PLUS];
    const options = {
      isSubscriptionOnly: true,
      productItemId,
      ...(isCollectionsCheckout ? { acquisitionType: 'COLLECTION' as const } : {}),
    };
    const additionalParams = isCollectionsCheckout ? { collectionId: resolvedCollectionId } : undefined;
    setIsSubmitting(true);
    setError(false);
    submitEnrollmentPromise({ handleSubmitPromise, options, additionalParams }).catch((apiError) => {
      loggerSingleton.error(`Failed to checkout Coursera Plus: ${apiError}`);
      setError(true);
      setIsSubmitting(false);
    });
  }, []);

  const handleEnrollInCourseraPlus = useCallback(
    (productItemIdParam?: string, collectionIdParam?: string) => {
      const productItemId = productItemIdParam ?? CourseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL;
      const resolvedCollectionId = collectionIdParam ?? collectionId;

      if (!isAuthenticatedUser()) {
        const queryParams = new URLSearchParams(search);
        Object.entries({
          authMode: AUTHENTICATION_MODAL_MODES.SIGNUP,
          action: ENROLL,
          [SKIP_ONBOARDING_QUERY_PARAM]: 'true',
          ...(shouldUseHookForPostAuthCheckout ? { variant: productItemId } : {}),
          ...(resolvedCollectionId ? { collectionId: resolvedCollectionId } : {}),
        }).forEach(([key, value]) => {
          if (value != null) {
            queryParams.set(key, String(value));
          }
        });
        navigate({ pathname, search: `?${queryParams.toString()}` }, { replace: true });
        return;
      }

      checkoutCourseraPlus(productItemId, resolvedCollectionId);
    },
    [checkoutCourseraPlus, collectionId, shouldUseHookForPostAuthCheckout, navigate, pathname, search]
  );

  return {
    isSubmitting,
    error,
    handleEnrollInCourseraPlus,
    checkoutCourseraPlus,
  };
};
